import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import imageCompression from "browser-image-compression";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

import { X, Plus, Images, GripVertical, Expand, Upload } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import Select from "../../../../components/input/select/Select";

import { generateUniqueId } from "../../../../utils/helpers/generator/generateUniqueId";
import { s3Uploader } from "../../../../utils/helpers/s3Uploader/s3Uploader";

import Axios from "../../../../api/axios/Axios";
import { useCreatorAlbums } from "../hooks/useAlbums";

import "./CreatorAlbumForm.css";

const MAX_PHOTOS = 100;

const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 1.5,
  maxWidthOrHeight: 2560,
  useWebWorker: true,
  initialQuality: 0.85,
  fileType: "image/jpeg",
};

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

const revokePhotoPreviews = (photos = []) => {
  photos.forEach((photo) => {
    if (photo.preview) {
      URL.revokeObjectURL(photo.preview);
    }
  });
};

function SortablePhoto({ photo, index, disabled, onRemove, onPreview }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 5 : 1,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        "creator-album-form__photo",
        isDragging && "creator-album-form__photo--dragging",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="creator-album-form__photo-preview"
        onClick={() => onPreview(photo.id)}
        disabled={disabled}
        aria-label={`Preview photo ${index + 1}`}
      >
        <img
          src={photo.preview}
          alt={`Album photo ${index + 1}`}
          draggable={false}
        />

        <span className="creator-album-form__expand">
          <Expand size={17} />
        </span>
      </button>

      <span className="creator-album-form__photo-number">{index + 1}</span>

      <button
        type="button"
        className="creator-album-form__photo-remove"
        onClick={() => onRemove(photo.id)}
        disabled={disabled}
        aria-label={`Remove photo ${index + 1}`}
      >
        <X size={16} />
      </button>

      <button
        type="button"
        className="creator-album-form__photo-drag"
        disabled={disabled}
        aria-label={`Reorder photo ${index + 1}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={18} />
      </button>

      <span className="creator-album-form__photo-size">{photo.sizeMB} MB</span>
    </div>
  );
}

export default function CreatorCreateAlbumForm({
  username,
  onClose,
  onSuccess,
}) {
  const { loading, error, createCreatorAlbum } = useCreatorAlbums(username);

  const [photos, setPhotos] = useState([]);
  const [processingImages, setProcessingImages] = useState(false);
  const [creatingAlbum, setCreatingAlbum] = useState(false);
  const [formError, setFormError] = useState("");
  const [previewId, setPreviewId] = useState(null);

  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("private");

  const fileInputRef = useRef(null);
  const photosRef = useRef([]);
  const busyRef = useRef(false);

  const isBusy = loading || processingImages || creatingAlbum;
  const canSubmit = photos.length > 0 && Boolean(title.trim()) && !isBusy;

  const previewPhoto = photos.find((photo) => photo.id === previewId) || null;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    busyRef.current = isBusy;
  }, [isBusy]);

  useEffect(() => {
    return () => {
      revokePhotoPreviews(photosRef.current);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (previewId !== null) {
        event.preventDefault();
        setPreviewId(null);
        return;
      }

      if (!busyRef.current) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewId, onClose]);

  const processImage = async (file) => {
    try {
      const compressedBlob = await imageCompression(
        file,
        IMAGE_COMPRESSION_OPTIONS,
      );

      const fileName = `${generateUniqueId(16)}.jpg`;

      const compressedFile = new File([compressedBlob], fileName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      return {
        id: generateUniqueId(8),
        file: compressedFile,
        preview: URL.createObjectURL(compressedFile),
        sizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
      };
    } catch (imageError) {
      console.error("Image processing failed:", imageError);
      return null;
    }
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);

    event.target.value = "";

    if (!files.length || isBusy) return;

    if (photos.length + files.length > MAX_PHOTOS) {
      setFormError(`An album can contain a maximum of ${MAX_PHOTOS} photos.`);
      return;
    }

    if (files.some((file) => !file.type.startsWith("image/"))) {
      setFormError("Only image files are supported.");
      return;
    }

    try {
      setProcessingImages(true);
      setFormError("");

      const processed = [];

      for (let i = 0; i < files.length; i += 4) {
        const batch = files.slice(i, i + 4);

        const results = await Promise.all(batch.map(processImage));

        processed.push(...results.filter(Boolean));
      }

      if (!processed.length) {
        setFormError("The selected images could not be processed.");
        return;
      }

      setPhotos((current) => [...current, ...processed]);

      if (processed.length < files.length) {
        setFormError("Some images could not be processed and were skipped.");
      }
    } catch (processingError) {
      console.error("Album image processing failed:", processingError);

      setFormError("Unable to process selected photos.");
    } finally {
      setProcessingImages(false);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id || isBusy) return;

    setPhotos((current) => {
      const oldIndex = current.findIndex((photo) => photo.id === active.id);

      const newIndex = current.findIndex((photo) => photo.id === over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return current;
      }

      return arrayMove(current, oldIndex, newIndex);
    });
  };

  const removePhoto = (id) => {
    if (isBusy) return;

    const photo = photos.find((item) => item.id === id);

    if (photo?.preview) {
      URL.revokeObjectURL(photo.preview);
    }

    setPhotos((current) => current.filter((item) => item.id !== id));

    if (previewId === id) {
      setPreviewId(null);
    }
  };

  const resetForm = () => {
    revokePhotoPreviews(photosRef.current);

    photosRef.current = [];

    setPhotos([]);
    setTitle("");
    setCaption("");
    setVisibility("private");
    setFormError("");
    setPreviewId(null);
  };

  const handleClose = () => {
    if (isBusy) return;

    resetForm();
    onClose?.();
  };

  const handleAddPhotos = () => {
    if (isBusy || photos.length >= MAX_PHOTOS) return;

    fileInputRef.current?.click();
  };

  const uploadPhoto = async (photo, index) => {
    const { data: uploadUrl } = await Axios.get("/api/creator/albums/s3-url");

    await s3Uploader(
      uploadUrl,
      {
        file: photo.file,
        filename: photo.file.name,
        contentType: photo.file.type,
      },
      0,
    );

    return {
      imageKey: photo.file.name,
      imageTag: generateUniqueId(8),
      displayOrder: index + 1,
    };
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!canSubmit) return;

    if (!username) {
      setFormError("Creator information is missing.");
      return;
    }

    try {
      setCreatingAlbum(true);
      setFormError("");

      const uploadedImages = [];

      for (let i = 0; i < photos.length; i += 4) {
        const batch = photos.slice(i, i + 4);

        const results = await Promise.all(
          batch.map((photo, batchIndex) => uploadPhoto(photo, i + batchIndex)),
        );

        uploadedImages.push(...results);
      }

      const result = await createCreatorAlbum({
        albumTag: generateUniqueId(8),
        title: title.trim(),
        caption: caption.trim(),
        visibility,
        sharedGroupIds: [],
        images: uploadedImages,
      });

      if (!result || result?.success === false) {
        setFormError(result?.message || "Failed to create album.");
        return;
      }

      await onSuccess?.(result);

      resetForm();
      onClose?.();
    } catch (submitError) {
      console.error("Album creation failed:", submitError);

      setFormError(
        submitError?.response?.data?.message ||
          submitError?.message ||
          "Unable to create album.",
      );
    } finally {
      setCreatingAlbum(false);
    }
  };

  const submitText = processingImages
    ? "Processing..."
    : creatingAlbum || loading
      ? "Creating..."
      : "Create Album";

  return createPortal(
    <div
      className="creator-album-form"
      role="dialog"
      aria-modal="true"
      aria-label="Create Album"
    >
      <header className="creator-album-form__header">
        <div className="creator-album-form__header-info">
          <h2>Create Album</h2>
          <p>Upload, preview and organise your photos.</p>
        </div>

        <div className="creator-album-form__header-actions">
          <span className="creator-album-form__counter">
            {photos.length} / {MAX_PHOTOS} photos
          </span>

          <button
            type="button"
            className="creator-album-form__close"
            onClick={handleClose}
            disabled={isBusy}
            aria-label="Close album form"
          >
            <X size={22} />
          </button>
        </div>
      </header>

      <div className="creator-album-form__workspace">
        <main className="creator-album-form__main">
          <div className="creator-album-form__toolbar">
            <div className="creator-album-form__toolbar-info">
              <h3>Album Photos</h3>
              <p>Drag photos to rearrange their order.</p>
            </div>

            <Button
              value="Add Photos"
              icon={<Plus size={17} />}
              variant="secondary"
              onClick={handleAddPhotos}
              disabled={isBusy || photos.length >= MAX_PHOTOS}
              style={{ width: "fit-content" }}
            />
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            disabled={isBusy}
            onChange={handleFileChange}
          />

          {photos.length === 0 ? (
            <button
              type="button"
              className="creator-album-form__empty"
              onClick={handleAddPhotos}
              disabled={isBusy}
            >
              <span className="creator-album-form__empty-icon">
                <Images size={38} strokeWidth={1.5} />
              </span>

              <strong>Add photos to your album</strong>

              <span>Select photos from your device to begin.</span>

              <span className="creator-album-form__empty-action">
                <Upload size={17} />
                Choose Photos
              </span>
            </button>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={photos.map((photo) => photo.id)}
                strategy={rectSortingStrategy}
              >
                <div className="creator-album-form__photo-grid">
                  {photos.map((photo, index) => (
                    <SortablePhoto
                      key={photo.id}
                      photo={photo}
                      index={index}
                      disabled={isBusy}
                      onRemove={removePhoto}
                      onPreview={setPreviewId}
                    />
                  ))}

                  {photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      className="creator-album-form__add-photo"
                      onClick={handleAddPhotos}
                      disabled={isBusy}
                    >
                      <Plus size={24} />
                      <span>Add More</span>
                    </button>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </main>

        <aside className="creator-album-form__sidebar">
          <div className="creator-album-form__sidebar-header">
            <h3>Album Details</h3>
            <p>Information about your album.</p>
          </div>

          <div className="creator-album-form__fields">
            <TextField
              label="Title"
              name="title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Give your album a title"
              maxLength={60}
              required
              disabled={isBusy}
            />

            <TextArea
              label="Caption"
              name="caption"
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
              placeholder="Tell viewers about this album"
              maxLength={2000}
              maxHeight={180}
              disabled={isBusy}
            />

            <Select
              label="Visibility"
              name="visibility"
              value={visibility}
              onChange={(event) => setVisibility(event.target.value)}
              items={VISIBILITY_OPTIONS}
              required
              disabled={isBusy}
            />
          </div>

          <div className="creator-album-form__summary">
            <h4>Upload Summary</h4>

            <div>
              <span>Photos</span>
              <strong>{photos.length}</strong>
            </div>

            <div>
              <span>Total size</span>
              <strong>
                {(
                  photos.reduce((total, photo) => total + photo.file.size, 0) /
                  (1024 * 1024)
                ).toFixed(2)}{" "}
                MB
              </strong>
            </div>
          </div>
        </aside>
      </div>

      <footer className="creator-album-form__footer">
        <div className="creator-album-form__footer-status">
          {(formError || error) && (
            <span className="creator-album-form__error" role="alert">
              {formError ||
                (typeof error === "string" ? error : error?.message)}
            </span>
          )}

          {!formError && !error && processingImages && (
            <span>Processing selected photos...</span>
          )}

          {!formError && !error && creatingAlbum && (
            <span>Uploading photos and creating album...</span>
          )}
        </div>

        <div className="creator-album-form__actions">
          <Button
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={isBusy}
          />

          <Button
            value={submitText}
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          />
        </div>
      </footer>

      {previewPhoto && (
        <div className="creator-album-form__lightbox">
          <button
            type="button"
            className="creator-album-form__lightbox-close"
            onClick={() => setPreviewId(null)}
            aria-label="Close photo preview"
          >
            <X size={24} />
          </button>

          <img
            src={previewPhoto.preview}
            alt="Full-size photo preview"
            draggable={false}
          />
        </div>
      )}
    </div>,
    document.body,
  );
}
